/**
 * Component: Location geocoding providers
 * Turns a US ZIP into a place, and a browser position into a place. Nothing else.
 *
 * Split out of hudson-location.js because this is the part that churns: vendor response shapes
 * change, providers get added or dropped, and none of it should be able to break the storage,
 * the showroom registry or the `?loc=` bootstrap that the header and the PDP row read.
 * Loaded deferred immediately before hudson-location.js, so document order guarantees it.
 *
 * LocationError and ERROR_CODES live here because the chain is what raises most of them;
 * hudson-location.js re-exports both so consumers see one vocabulary.
 */
window.HudsonLocationProviders =
  window.HudsonLocationProviders ||
  (() => {
    const LABEL = "[location]";
    const REQUEST_TIMEOUT_MS = 6000;
    const LOOKUP_TIMEOUT_MS = 10000;

    class LocationError extends Error {
      constructor(code, message) {
        super(message || code);
        this.name = "LocationError";
        this.code = code;
      }
    }

    const ERROR_CODES = Object.freeze({
      INVALID_ZIP: "INVALID_ZIP",
      NOT_FOUND: "NOT_FOUND",
      NETWORK: "NETWORK",
      TIMEOUT: "TIMEOUT",
      PERMISSION_DENIED: "PERMISSION_DENIED",
      POSITION_UNAVAILABLE: "POSITION_UNAVAILABLE",
      INSECURE_CONTEXT: "INSECURE_CONTEXT",
      UNSUPPORTED: "UNSUPPORTED",
      NO_SHOWROOM: "NO_SHOWROOM",
    });

    function abortableFetch(url, callerSignal) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      const relay = () => controller.abort();
      callerSignal?.addEventListener("abort", relay, { once: true });

      return fetch(url, { signal: controller.signal, credentials: "omit", mode: "cors" }).finally(
        () => {
          clearTimeout(timer);
          callerSignal?.removeEventListener("abort", relay);
        },
      );
    }

    const toPlace = (zip, city, region, lat, lng) => ({
      zip: String(zip || "").trim(),
      city: String(city || "").trim(),
      region: String(region || "").trim(),
      lat: Number(lat),
      lng: Number(lng),
    });

    function parseGoogleResult(result) {
      const components = result.address_components || [];
      const pick = (...types) => {
        for (const type of types) {
          const match = components.find((part) => part.types?.includes(type));
          if (match) return match;
        }
        return null;
      };

      const location = result.geometry?.location || {};

      return toPlace(
        pick("postal_code")?.long_name,
        /* Rural ZIPs often carry no locality, so the looser administrative names stand in. */
        pick("locality", "postal_town", "sublocality", "neighborhood")?.long_name,
        pick("administrative_area_level_1")?.short_name,
        location.lat,
        location.lng,
      );
    }

    /**
     * Google answers HTTP 200 for its own errors, so data.status is the only real result.
     * ZERO_RESULTS is a definitive "no such ZIP" and ends the chain; anything else is this
     * provider failing and the next one gets a turn.
     */
    async function fetchGoogleGeocode(url, signal) {
      const response = await abortableFetch(url, signal);
      if (!response.ok) return null;

      const data = await response.json();
      if (data.status === "ZERO_RESULTS") throw new LocationError(ERROR_CODES.NOT_FOUND);

      if (data.status !== "OK" || !data.results?.length) {
        console.warn(`${LABEL} Google geocoding returned ${data.status}, trying the next provider.`);
        return null;
      }

      return parseGoogleResult(data.results[0]);
    }

    function createGoogleProvider(apiKey) {
      const key = encodeURIComponent(apiKey);

      return {
        id: "google",
        available: () => Boolean(apiKey),

        forwardZip(zip, signal) {
          return fetchGoogleGeocode(
            `https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:${encodeURIComponent(zip)}|country:US&key=${key}`,
            signal,
          );
        },

        async reverse(lat, lng, signal) {
          const place = await fetchGoogleGeocode(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=postal_code&key=${key}`,
            signal,
          );
          /* result_type=postal_code answers with the ZIP centroid; the browser position is better. */
          return place && { ...place, lat, lng };
        },
      };
    }

    const zippopotamProvider = {
      id: "zippopotam",
      available: () => true,

      async forwardZip(zip, signal) {
        const response = await abortableFetch(`https://api.zippopotam.us/us/${zip}`, signal);
        /* 404 here means "no such ZIP" — an answer, not a provider failure. */
        if (response.status === 404) throw new LocationError(ERROR_CODES.NOT_FOUND);
        if (!response.ok) return null;

        const data = await response.json();
        const place = data.places?.[0];
        if (!place) throw new LocationError(ERROR_CODES.NOT_FOUND);

        return toPlace(
          data["post code"],
          place["place name"],
          place["state abbreviation"],
          place.latitude,
          place.longitude,
        );
      },
    };

    const bigDataCloudProvider = {
      id: "bigdatacloud",
      available: () => true,

      async reverse(lat, lng, signal) {
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
        const response = await abortableFetch(url, signal);
        if (!response.ok) return null;

        const data = await response.json();
        if (!data.postcode) throw new LocationError(ERROR_CODES.NOT_FOUND);

        return toPlace(
          data.postcode,
          data.city || data.locality,
          String(data.principalSubdivisionCode || "").split("-").pop(),
          lat,
          lng,
        );
      },
    };

    /**
     * Walks the chain until a provider answers. A LocationError is the provider's final word and
     * ends the walk; anything else is that provider failing, and the next one gets a turn.
     */
    async function runChain(providers, attempt) {
      const deadline = Date.now() + LOOKUP_TIMEOUT_MS;
      let lastFailure = null;

      for (const provider of providers) {
        if (!provider.available() || Date.now() > deadline) continue;

        try {
          const place = await attempt(provider);
          if (place && place.zip) return place;
        } catch (error) {
          if (error instanceof LocationError) throw error;
          if (error.name === "AbortError") throw error;
          lastFailure = error;
          console.warn(`${LABEL} ${provider.id} could not answer, trying the next provider.`, error);
        }
      }

      throw new LocationError(lastFailure ? ERROR_CODES.NETWORK : ERROR_CODES.NOT_FOUND);
    }

    function create({ apiKey = "" } = {}) {
      const google = createGoogleProvider(apiKey);
      const forward = [google, zippopotamProvider];
      const reverse = [google, bigDataCloudProvider];

      return Object.freeze({
        lookupZip: (zip, signal) => runChain(forward, (provider) => provider.forwardZip(zip, signal)),
        reverse: (lat, lng, signal) =>
          runChain(reverse, (provider) => provider.reverse(lat, lng, signal)),
      });
    }

    return Object.freeze({ create, LocationError, ERROR_CODES });
  })();
